import React from 'react';
import { useTranslation } from '@shared/core';

export type ChunkErrorFallbackProps = Readonly<{
  /** True when the underlying error is a failed chunk load (vs. a generic render error). */
  isChunkLoadError: boolean;
  /** Reloads the page (cache-busting is handled by the boundary). */
  onReload: () => void;
}>;

/**
 * Default section-level fallback for {@link ChunkErrorBoundary}.
 *
 * Split out as a function component so it can translate its copy via `tq` —
 * the boundary itself is a class component (error boundaries must be) and can't
 * use hooks. This is safe because a section-level chunk failure is caught
 * *inside* the guest app providers (i18n, theme), so the fallback still has a
 * live i18n context. The top-level app `ErrorBoundary` renders above those
 * providers and therefore keeps hard-coded English copy.
 *
 * Uses inline styles only — no joykit / styled-components — so the fallback
 * renders even if a design-system chunk is the thing that failed.
 */
export const ChunkErrorFallback: React.FC<ChunkErrorFallbackProps> = ({ isChunkLoadError, onReload }) => {
  // Reuse the shared `errors` namespace (also home to ComponentHealth's error UI)
  // rather than minting a new namespace for a couple of fallback strings.
  const { tq } = useTranslation('errors');

  return (
    <div style={styles.container}>
      <p style={styles.message}>
        {isChunkLoadError ? tq`This section couldn’t load. ` : tq`Something went wrong loading this section. `}
        <button
          type="button"
          onClick={onReload}
          style={styles.link}
        >
          {tq`Reload page`}
        </button>
      </p>
    </div>
  );
};

// Inline styles — must work without joykit or styled-components.
// Intentionally minimal so section fallbacks don't dominate the page.
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    width: '100%',
  },
  message: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
    textAlign: 'center' as const,
  },
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#4951EF',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textDecoration: 'underline',
  },
};
